import { type ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import Buttons from '@/components/Buttons';
import CollapsibleArea from '@/components/CollapsibleArea';
import Button from '@/components/DesignLibrary/Button';
import ListCell from '@/components/DesignLibrary/ListCell';
import PlusCircle from '@/components/Icons/PlusCircle';
import Spacer from '@/components/Spacer';
import useNoodleApi from '@/hooks/useNoodleApi';
import TeamsContext from '@/providers/Teams/TeamsContext';
import * as tsClient from '@/tsClient';
import { getCreatorTeam } from '@/tsClient/creators';
import { type WorkflowOwner } from '@/tsClient/servers/noodleApi/ApiDefinition/workflows';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES, UserWorkflowCollaboratorSourceEnum } from '@/typings/api-models';

import SearchInput from '../FormFields/SearchInput';
import NothingToDisplay from '../NothingToDisplay';

import s from './AssignCollaboratorsToAttachmentForm.module.scss';

export type Collaborator = {
  id: string;
  logo?: string | null;
  color?: string | null;
  name?: string | null;
  email?: string | null;
  defaultWorkflowRole?: string | null;
  source?: string | null;
};

type Props = {
  attachmentType?: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
  attachmentReferenceId?: string;
  collaborators: Collaborator[];
  isSubmitting: boolean;
  onSubmit: (owners: WorkflowOwner[]) => void;
  onInviteClick?: () => void;
  initialCollaborators?: WorkflowOwner[];
  hasPadding?: boolean;
  disableAssignCollaboratorsToModal?: boolean;
  priorityRoleId?: string | null;
  disableFetchTeamMembers?: boolean;
  showAssignedOwnersSection?: boolean;
};

const AssignCollaboratorsToAttachmentForm = ({
  onSubmit,
  onInviteClick,
  isSubmitting,
  collaborators,
  attachmentReferenceId,
  attachmentType,
  initialCollaborators,
  hasPadding = true,
  disableAssignCollaboratorsToModal = false,
  priorityRoleId,
  disableFetchTeamMembers = false,
  showAssignedOwnersSection = false,
}: Props): ReactElement => {
  const [assignedCollaboratorIds, setAssignedCollaboratorIds] = useState<string[]>([]);
  const [assignedCollaborators, setAssignedCollaborators] = useState<Collaborator[]>([]);
  const [search, setSearch] = useState('');
  const [isManualAttorneySignature, setIsManualAttorneySignature] = useState<boolean>(false);

  const { workflowRoles, fetchWorkflowRoles, creatorId } = useContext(TeamsContext);
  const { getData: getTeamMembersFn, data: teamMembers } = useNoodleApi(getCreatorTeam);

  const { getData: getDocumentRequestUserPermissionsFn } = useNoodleApi(
    tsClient.documentRequests.getDocumentRequestUserPermissions,
  );
  const { getData: getFormRequestPermissionsFn } = useNoodleApi(tsClient.formRequests.getFormRequestPermissions);
  const { getData: getInvoicePermissions } = useNoodleApi(tsClient.invoices.getInvoicePermissions);
  const { getData: getCustomTermsPermissions } = useNoodleApi(tsClient.customTerms.getCustomTermsPermissions);
  const { getData: getBookingPermissions } = useNoodleApi(tsClient.scheduling.getBookingPermissions);
  const { getData: getCreditReportPermissionsFn } = useNoodleApi(tsClient.creditReportsB2C.getCreditReportPermissions);
  const { getData: getBankAccountIntegrationPermissions } = useNoodleApi(
    tsClient.bankAccountIntegrations.getBankAccountIntegrationPermissions,
  );
  const { getData: getESignatureRequestPermissionsFn } = useNoodleApi(
    tsClient.eSignatureRequests.getESignatureRequestPermissions,
  );
  const { getData: getPaymentGateAssignees } = useNoodleApi(tsClient.workflows.getPaymentGateAssignees);
  const { getData: getCustomTerms } = useNoodleApi(tsClient.customTerms.getCustomTerms);
  const { getData: getCustomTermsTemplateById } = useNoodleApi(tsClient.customTerms.getCustomTermsTemplateById);

  // Helper function to check if a collaborator is an attorney
  const isAttorney = useCallback(
    (collaborator: Collaborator): boolean => {
      if (!collaborator.defaultWorkflowRole) {
        return false;
      }
      const role = workflowRoles?.find((r) => r.id === collaborator.defaultWorkflowRole);
      return role?.name?.toLowerCase().includes('attorney') ?? false;
    },
    [workflowRoles],
  );

  useEffect(() => {
    const getAssignedCollaborators = async (): Promise<void> => {
      if (!attachmentType || !attachmentReferenceId) {
        return;
      }
      if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER) {
        const { data: documentRequestPermissions } = await getDocumentRequestUserPermissionsFn({
          id: attachmentReferenceId,
        });
        if (documentRequestPermissions) {
          const assignedUserIds = documentRequestPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT) {
        const { data: creditReportPermissions } = await getCreditReportPermissionsFn({
          creditReportId: attachmentReferenceId,
        });
        if (creditReportPermissions) {
          const assignedUserIds = creditReportPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST) {
        const { data: formRequestPermissions } = await getFormRequestPermissionsFn({
          formRequestId: attachmentReferenceId,
        });
        if (formRequestPermissions) {
          const assignedUserIds = formRequestPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE) {
        const { data: invoicePermissions } = await getInvoicePermissions({ invoiceId: attachmentReferenceId });
        if (invoicePermissions) {
          const assignedUserIds = invoicePermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS) {
        const { data: termsPermissions } = await getCustomTermsPermissions({ id: attachmentReferenceId });
        if (termsPermissions) {
          const assignedUserIds = termsPermissions.map((p) => p.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
        // Fetch customTerms to get template ID, then fetch template to check manualAttorneySignature
        const { data: customTermsData } = await getCustomTerms({ id: attachmentReferenceId });
        if (customTermsData?.template?.id) {
          const { data: templateData } = await getCustomTermsTemplateById({ id: customTermsData.template.id });
          setIsManualAttorneySignature(templateData?.manualAttorneySignature ?? false);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING) {
        const { data: bookingPermissions } = await getBookingPermissions({ id: attachmentReferenceId });
        if (bookingPermissions) {
          const assignedUserIds = bookingPermissions.map((p) => p.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION) {
        const { data: bankAccountIntegrationPermissions } = await getBankAccountIntegrationPermissions({
          bankAccountIntegrationId: attachmentReferenceId,
        });
        if (bankAccountIntegrationPermissions) {
          const assignedUserIds = bankAccountIntegrationPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (
        attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.E_SIGNATURE_REQUEST &&
        !disableAssignCollaboratorsToModal
      ) {
        const { data: eSignatureRequestPermissions } = await getESignatureRequestPermissionsFn({
          eSignatureRequestId: attachmentReferenceId,
        });
        if (eSignatureRequestPermissions) {
          const assignedUserIds = eSignatureRequestPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_GATE) {
        const { data } = await getPaymentGateAssignees({
          paymentGateId: attachmentReferenceId,
        });
        setAssignedCollaboratorIds(data?.userIds || []);
      }
    };
    getAssignedCollaborators();
  }, [
    getInvoicePermissions,
    getDocumentRequestUserPermissionsFn,
    getFormRequestPermissionsFn,
    getCreditReportPermissionsFn,
    attachmentType,
    attachmentReferenceId,
    getCustomTermsPermissions,
    getBookingPermissions,
    getBankAccountIntegrationPermissions,
    getESignatureRequestPermissionsFn,
    getPaymentGateAssignees,
    disableAssignCollaboratorsToModal,
    getCustomTerms,
    getCustomTermsTemplateById,
  ]);

  useEffect(() => {
    setAssignedCollaborators(collaborators.filter((c) => assignedCollaboratorIds.includes(c.id)));
  }, [assignedCollaboratorIds, collaborators]);

  // Auto-select initial collaborators when they are provided
  useEffect(() => {
    if (initialCollaborators && initialCollaborators.length > 0) {
      const initialCollaboratorIds = initialCollaborators.map((c) => c.personId);
      setAssignedCollaboratorIds((prev) => {
        // Merge with existing IDs to avoid duplicates
        const existingIds = new Set(prev);
        initialCollaboratorIds.forEach((id) => existingIds.add(id));
        return Array.from(existingIds);
      });
    }
  }, [initialCollaborators]);

  useEffect(() => {
    fetchWorkflowRoles();
  }, [fetchWorkflowRoles]);

  useEffect(() => {
    if (creatorId && !disableFetchTeamMembers) {
      getTeamMembersFn({ creatorId, page: 1, perPage: 1000 });
    }
  }, [getTeamMembersFn, creatorId, disableFetchTeamMembers]);

  /**
   * Sorts collaborators array by prioritizing initial collaborators first, then alphabetically.
   * @param cc - Array of collaborators to sort
   * @param icc - Optional array of initial collaborators to prioritize
   * @returns Sorted array with initial collaborators first, then others alphabetically by name
   */
  const sortCollaborators = useCallback((cc: Collaborator[], icc?: WorkflowOwner[]): Collaborator[] => {
    if (!icc) return [...cc].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    const initialCollaboratorIds = icc.map((c) => c.personId);
    return [...cc].sort((a, b) => {
      const aIsInitial = initialCollaboratorIds.includes(a.id);
      const bIsInitial = initialCollaboratorIds.includes(b.id);

      if (aIsInitial && !bIsInitial) return -1;
      if (!aIsInitial && bIsInitial) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, []);

  /**
   *
   * @param cc - Array of collaborators to categorize
   * @param workflowRoles - Array of workflow roles to categorize collaborators
   * @returns Object with workflow role IDs as properties and unassigned key, each property is an array of collaborators
   */
  const categorizeCollaborators = useCallback(
    (cc: Collaborator[]): Record<string, Collaborator[]> => {
      // Create object with all workflowRoleIds as properties and unassigned key, initialize to empty arrays
      const categorizedObject: Record<string, Collaborator[]> = {};

      // Add assignedOwners section if enabled and there are initial collaborators
      if (showAssignedOwnersSection && initialCollaborators && initialCollaborators.length > 0) {
        categorizedObject.assignedOwners = [];
      }

      // Add all workflow role IDs as properties
      if (workflowRoles) {
        workflowRoles.forEach((role) => {
          categorizedObject[role.id] = [];
        });
      }
      // Add customer key, used for e-signature where customers are part of collaborators
      categorizedObject.customer = [];

      // Add unassigned key
      categorizedObject.unassigned = [];

      // Get initial collaborator IDs for quick lookup
      const initialCollaboratorIds = new Set(initialCollaborators?.map((ic) => ic.personId) || []);

      // Loop through collaborators and assign them to appropriate arrays
      cc.forEach((collaborator) => {
        // If showing assigned owners section and this collaborator is in the initial list, add to assignedOwners
        if (showAssignedOwnersSection && initialCollaboratorIds.has(collaborator.id)) {
          categorizedObject.assignedOwners?.push(collaborator);
        }

        // First check if collaborator has a specific workflow role
        if (collaborator.defaultWorkflowRole && categorizedObject[collaborator.defaultWorkflowRole]) {
          categorizedObject[collaborator.defaultWorkflowRole].push(collaborator);
        } else {
          // If no specific workflow role, categorize by source
          const isClient =
            collaborator.source === UserWorkflowCollaboratorSourceEnum.enum.customer ||
            collaborator.source === UserWorkflowCollaboratorSourceEnum.enum['manually-added'] ||
            collaborator.source === UserWorkflowCollaboratorSourceEnum.enum['context-person'];

          if (isClient) {
            categorizedObject.customer.push(collaborator);
          } else {
            categorizedObject.unassigned.push(collaborator);
          }
        }
      });

      // Sort each array using sortCollaborators function
      Object.keys(categorizedObject).forEach((roleId) => {
        categorizedObject[roleId] = sortCollaborators(categorizedObject[roleId], initialCollaborators);
      });

      return categorizedObject;
    },
    [sortCollaborators, workflowRoles, initialCollaborators, showAssignedOwnersSection],
  );

  // Transform team members to collaborator format for proper categorization
  // Memoize to prevent useMemo dependencies from changing on every render
  const teamMemberCollaborators = useMemo(
    () =>
      teamMembers?.items?.map((member) => ({
        color: member.primaryColour?.hex || null,
        defaultWorkflowRole: member.defaultWorkflowRole || null,
        email: member.email || null,
        id: member.id,
        logo: member.image?.url || null,
        name: member.name || null,
        source: 'creator-team-member' as const,
      })) || [],
    [teamMembers],
  );

  const effectiveCollaborators = useMemo(() => {
    const teamMemberIds = new Set(teamMemberCollaborators.map((member) => member.id));
    const uniqueCollaborators = collaborators.filter((collaborator) => !teamMemberIds.has(collaborator.id));
    return [...teamMemberCollaborators, ...uniqueCollaborators];
  }, [teamMemberCollaborators, collaborators]);

  const flipCollaboratorAssignment = useCallback(
    (collaborator: Collaborator): void => {
      if (assignedCollaboratorIds.includes(collaborator.id)) {
        setAssignedCollaboratorIds((prev) => prev.filter((id) => id !== collaborator.id));
        return;
      }
      // If manualAttorneySignature is true and this is an attorney, only allow 1 attorney selection
      if (isManualAttorneySignature && isAttorney(collaborator)) {
        // Remove any existing attorney selections, then add the new one
        const currentCollaborators = effectiveCollaborators.filter((c) => assignedCollaboratorIds.includes(c.id));
        const nonAttorneyIds = currentCollaborators.filter((c) => !isAttorney(c)).map((c) => c.id);
        setAssignedCollaboratorIds([...nonAttorneyIds, collaborator.id]);
        return;
      }
      setAssignedCollaboratorIds((prev) => [...prev, collaborator.id]);
    },
    [assignedCollaboratorIds, isManualAttorneySignature, isAttorney, effectiveCollaborators],
  );

  // Ensure only 1 attorney selection when manualAttorneySignature is true
  useEffect(() => {
    if (isManualAttorneySignature && assignedCollaboratorIds.length > 0) {
      const currentCollaborators = effectiveCollaborators.filter((c) => assignedCollaboratorIds.includes(c.id));
      const attorneyCollaborators = currentCollaborators.filter((c) => isAttorney(c));
      if (attorneyCollaborators.length > 1) {
        // Keep only the first attorney and all non-attorneys
        const firstAttorneyId = attorneyCollaborators[0].id;
        const nonAttorneyIds = currentCollaborators.filter((c) => !isAttorney(c)).map((c) => c.id);
        setAssignedCollaboratorIds([...nonAttorneyIds, firstAttorneyId]);
      }
    }
  }, [isManualAttorneySignature, assignedCollaboratorIds, effectiveCollaborators, workflowRoles]);

  const categorizedCollaborators = useMemo(
    () => categorizeCollaborators(effectiveCollaborators),
    [categorizeCollaborators, effectiveCollaborators],
  );

  // Memoize the sorted categories so priority role stays at top
  const sortedCategories = useMemo(
    () =>
      Object.entries(categorizedCollaborators).sort(([categoryIdA], [categoryIdB]) => {
        // Assigned owners always first if it exists
        if (categoryIdA === 'assignedOwners') return -1;
        if (categoryIdB === 'assignedOwners') return 1;

        // Priority role always first (after assigned owners)
        if (priorityRoleId) {
          if (categoryIdA === priorityRoleId) return -1;
          if (categoryIdB === priorityRoleId) return 1;
        }

        // Clients always first (after priority role)
        if (categoryIdA === 'customer') return -1;
        if (categoryIdB === 'customer') return 1;

        // Other team members always last
        if (categoryIdA === 'unassigned') return 1;
        if (categoryIdB === 'unassigned') return -1;

        // Sort workflow roles by rank
        const roleA = workflowRoles?.find((role) => role.id === categoryIdA);
        const roleB = workflowRoles?.find((role) => role.id === categoryIdB);

        if (roleA && roleB) {
          return (roleA.rank || 0) - (roleB.rank || 0);
        }

        // Fallback to alphabetical
        return categoryIdA.localeCompare(categoryIdB);
      }),
    [categorizedCollaborators, priorityRoleId, workflowRoles],
  );

  // Returns the human-friendly category name used in the UI
  const getCategoryDisplayName = (categoryId: string): string => {
    if (categoryId === 'assignedOwners') return 'Assigned Owners';
    if (categoryId === 'unassigned') return 'Other Team Members';
    if (categoryId === 'customer') return 'Clients';
    const roleName = workflowRoles?.find((role) => role.id === categoryId)?.name || categoryId;
    // If this is for customTerms with manualAttorneySignature and the role contains "attorney", change to "countersigning attorney"
    if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS && isManualAttorneySignature) {
      if (roleName === 'Attorney') {
        return 'Countersigning Attorney';
      }
    }
    return roleName;
  };

  // Lowercased search term used across categories
  const lowerSearch = search.toLowerCase();

  return (
    <>
      <div className={s.assignCollaborator} style={{ padding: hasPadding ? 16 : 0 }}>
        {effectiveCollaborators.length > 0 ? (
          <div className={s['collaborators-container']}>
            <div className={s.searchContainer}>
              <SearchInput
                label='Search Collaborators'
                hideLabel
                setSearch={setSearch}
                placeholder='filter by team, name or email'
                className={s.search}
              />
            </div>
            {
              // Use the memoized sorted categories
              sortedCategories.map(([categoryId, categoryCollaborators]) => {
                // Compute display name and filter collaborators in this category based on search
                const categoryName = getCategoryDisplayName(categoryId);
                const categoryNameLower = categoryName.toLowerCase();
                const filteredCollaborators = categoryCollaborators.filter((collaborator) => {
                  const emailLower = collaborator.email?.toLowerCase() ?? '';
                  const nameLower = collaborator.name?.toLowerCase() ?? '';
                  return (
                    emailLower.includes(lowerSearch) ||
                    nameLower.includes(lowerSearch) ||
                    categoryNameLower.includes(lowerSearch)
                  );
                });

                // Don't render category if no collaborators match the search
                if (filteredCollaborators.length === 0) return null;

                // categoryName already computed above for both filtering and rendering

                // Get selected collaborators for this category
                const selectedInCategory = filteredCollaborators.filter((c) => assignedCollaboratorIds.includes(c.id));
                const selectedText = `${selectedInCategory.length}/${filteredCollaborators.length}: ${selectedInCategory.map((c) => c.name || '').join(', ')}`;

                // Determine if this section should be open
                const hasAssignedOwnersSection = 'assignedOwners' in categorizedCollaborators;
                const isOpen = hasAssignedOwnersSection
                  ? categoryId === 'assignedOwners' // Only assignedOwners is open when it exists
                  : priorityRoleId && priorityRoleId in categorizedCollaborators
                    ? categoryId === priorityRoleId
                    : true;

                return (
                  <CollapsibleArea
                    key={`${categoryId}-${priorityRoleId || 'none'}`}
                    title={categoryName}
                    description={selectedInCategory.length > 0 ? selectedText : 'No collaborators selected'}
                    open={isOpen}
                    className={s.categoryAccordion}
                  >
                    <div className={s.categoryContent}>
                      {filteredCollaborators.map((collaborator) => {
                        // For assignedOwners category, show "email - role" format
                        let bottomLine = collaborator.email || '';
                        if (categoryId === 'assignedOwners' && collaborator.defaultWorkflowRole) {
                          const roleName = workflowRoles?.find(
                            (role) => role.id === collaborator.defaultWorkflowRole,
                          )?.name;
                          if (roleName) {
                            bottomLine = `${collaborator.email || ''} - ${roleName}`;
                          }
                        }

                        return (
                          <ListCell
                            key={collaborator.id}
                            avatar={{
                              color: collaborator.color || '',
                              name: collaborator.name || '',
                              url: collaborator.logo || '',
                            }}
                            label={collaborator.name || 'No name'}
                            bottomLine={bottomLine}
                            showCheckbox
                            id={collaborator.id}
                            selectedId={assignedCollaboratorIds.includes(collaborator.id) ? collaborator.id : undefined}
                            onClick={() => flipCollaboratorAssignment(collaborator)}
                          />
                        );
                      })}
                    </div>
                  </CollapsibleArea>
                );
              })
            }
          </div>
        ) : (
          <NothingToDisplay isBordered text='No collaborators found' />
        )}
        {onInviteClick && (
          <>
            <Spacer size={16} />
            <Buttons isExtraSmall isWrapper className={s.invite} onClick={onInviteClick}>
              <>
                <PlusCircle size={8} weight='fill' />
                Invite Collaborator and Assign
              </>
            </Buttons>
          </>
        )}
      </div>
      <div className={s.buttonContainer}>
        <Button
          variant='primary'
          size='md'
          onClick={() => {
            onSubmit(
              assignedCollaborators.map((collaborator) => {
                // Preserve existing workflow roles for this collaborator if they were initially assigned
                const existingOwner = initialCollaborators?.find((owner) => owner.personId === collaborator.id);
                return {
                  personId: collaborator.id,
                  workflowRoles:
                    existingOwner?.workflowRoles ||
                    (collaborator.defaultWorkflowRole ? [collaborator.defaultWorkflowRole] : []),
                };
              }),
            );
          }}
          loading={isSubmitting}
          fullWidth
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default AssignCollaboratorsToAttachmentForm;
